import { ControlledMultiSelect, MultiLineInput } from '@ui-kit'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import {
  addToaster,
  DropdownOptionsType,
  EntityFieldsObjectTypes,
  getEntityById,
  hideLoader,
  showLoader,
} from '@app-store'
import {
  editEntityData,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { v4 as uuid } from 'uuid'
import { useAppDispatch } from '../../utils/agent.store'
import { PAPReviewRequestCustomPropsType } from '../../types/pap.types'
import {
  PAP_NAMESPACES,
  REVIEW_REQUEST_CUSTOM_LAYOUT,
} from '../../utils/pap.constants'
import CategoryInput from '../common/CategoryInput'

const PAPReviewRequestCustom = forwardRef(
  (
    {
      papDetailsId,
      task,
      recordId,
      isViewMode = false,
      setIsViewMode,
    }: PAPReviewRequestCustomPropsType,
    ref
  ) => {
    const [papDetailData, setPapDetailData] = useState<FieldValues | null>(null)
    const [filteredFieldsMap, setFilteredFieldsMap] = useState<Record<
      string,
      EntityFieldsObjectTypes
    > | null>(null)

    const { register, setValue, handleSubmit, reset, control } = useForm()
    const dispatch = useAppDispatch()

    const onSubmit = async (data: FieldValues) => {
      try {
        dispatch(showLoader())
        delete data.lookupLabels
        const res = await editEntityData(
          task?.identifier?.layout_info?.case?.entity_name ?? '',
          Number(task?.identifier?.layout_info?.case?.entity_id),
          1, // Random layoutId
          {
            case_pap_details: { ...data, id: papDetailsId },
          },
          recordId
        )
        if (res.data) {
          fetchPAPDetails()
          dispatch(
            addToaster({
              message: PAP_NAMESPACES?.DATA_ADDED_SUCCESSFULLY,
              type: 'success',
              id: uuid(),
            })
          )
        }
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    }

    const fetchPAPDetails = async () => {
      try {
        if (!papDetailsId) return
        const res = await getEntityDataByIdEmbedded(
          'case_pap_details',
          2259,
          [
            REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY,
            REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY,
            REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY,
            REVIEW_REQUEST_CUSTOM_LAYOUT.SUMMARY_KEY,
          ],
          papDetailsId
        )
        if (res.data) {
          setPapDetailData(res.data)
          reset(res.data)
          setIsViewMode(true)
        }
      } catch (err) {
        console.error(err)
      }
    }

    const fetchColMeta = async () => {
      try {
        const res = await getEntityById({ entityId: 2259 })
        if (res?.data) {
          const allFields = [
            REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY,
            REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY,
            REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY,
            REVIEW_REQUEST_CUSTOM_LAYOUT.SUMMARY_KEY,
          ]

          const filteredFieldsObject = res.data.fields
            .filter((field: EntityFieldsObjectTypes) =>
              allFields.includes(field?.name ?? '')
            )
            .reduce(
              (
                acc: Record<string, EntityFieldsObjectTypes>,
                field: EntityFieldsObjectTypes
              ) => {
                acc[field?.name ?? ''] = field
                return acc
              },
              {}
            )

          setFilteredFieldsMap(filteredFieldsObject)
        }
      } catch (err) {
        console.error(err)
      }
    }

    useImperativeHandle(ref, () => ({
      submitForm: () => handleSubmit(onSubmit)(),
    }))

    useEffect(() => {
      fetchPAPDetails()
    }, [papDetailsId])

    useEffect(() => {
      fetchColMeta()
    }, [])

    return !isViewMode ? (
      <form onSubmit={handleSubmit(onSubmit)} className="p-3">
        <div className="flex gap-2">
          <CategoryInput
            control={control}
            label={
              filteredFieldsMap?.[
                REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY
              ]?.label ?? ''
            }
            name={REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY}
            onChange={(e: DropdownOptionsType) => {
              setValue(
                REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY,
                e.value
              )
            }}
            categoryKey={
              filteredFieldsMap?.[
                REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY
              ]?.filter_criteria?.category_type ?? ''
            }
            setValue={setValue}
          />
          <CategoryInput
            control={control}
            label={
              filteredFieldsMap?.[REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY]
                ?.label ?? ''
            }
            name={REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY}
            onChange={(e: DropdownOptionsType) => {
              setValue(REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY, e.value)
            }}
            setValue={setValue}
            categoryKey={
              filteredFieldsMap?.[REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY]
                ?.filter_criteria?.category_type ?? ''
            }
          />
          <ControlledMultiSelect
            control={control}
            label={
              filteredFieldsMap?.[REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY]
                ?.label ?? ''
            }
            options={
              filteredFieldsMap?.[
                REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY
              ]?.control_values?.map((value) => ({
                label: value,
                value,
              })) ?? []
            }
            name={REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY}
            onChange={(e: DropdownOptionsType[]) => {
              setValue(
                REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY,
                e.map((item) => item.value)
              )
            }}
          />
        </div>
        <div className="pt-3">
          <MultiLineInput
            label={
              filteredFieldsMap?.[REVIEW_REQUEST_CUSTOM_LAYOUT.SUMMARY_KEY]
                ?.label ?? ''
            }
            name={REVIEW_REQUEST_CUSTOM_LAYOUT.SUMMARY_KEY}
            register={register}
          />
        </div>
      </form>
    ) : (
      <div className="p-4 w-full flex flex-col  gap-4">
        <div className="flex gap-4 justify-between w-full">
          <div className="flex flex-col gap-1">
            <span className="font-primary text-sm font-normal">
              {REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_LABEL}
            </span>
            <span className="font-primary text-xs font-extralight">
              {
                papDetailData?.[
                  REVIEW_REQUEST_CUSTOM_LAYOUT.REVIEW_COMPLETED_KEY
                ]?.value
              }
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-primary text-sm font-normal">
              {REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_LABEL}
            </span>
            <span className="font-primary text-xs font-extralight">
              {
                papDetailData?.[REVIEW_REQUEST_CUSTOM_LAYOUT.DECISION_KEY]
                  ?.value
              }
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-primary text-sm font-normal">
              {REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_LABEL}
            </span>
            <span className="font-primary text-xs font-extralight">
              {papDetailData?.[REVIEW_REQUEST_CUSTOM_LAYOUT.REASON_KEY]?.join(
                ', '
              ) || ''}
            </span>
          </div>
        </div>
        <div>
          <div className="flex flex-col gap-1">
            <span className="font-primary text-sm font-normal">
              {REVIEW_REQUEST_CUSTOM_LAYOUT.SUMMARY_LABEL}
            </span>
            <span className="font-primary text-xs font-extralight">
              {papDetailData?.[REVIEW_REQUEST_CUSTOM_LAYOUT.SUMMARY_KEY]}
            </span>
          </div>
        </div>
      </div>
    )
  }
)

PAPReviewRequestCustom.displayName = 'PAPReviewRequestCustom'

export default PAPReviewRequestCustom
